/* eslint-disable max-len */
import { Form, Formik } from 'formik';
import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StyledSingleDropdown from 'src/components/StyledSingleDropdown/StyledSingleDropdown';
import { TooltipAnchor } from 'src/services/app.service';
import { appRoutePaths } from 'src/services/route.service';
import {
  DyslexiaScreenerListDisplayRowModel,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_1,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_KG,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_TRAINING,
  DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE,
  DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED,
} from 'src/types/DyslexiaScreenerListDisplayRowModel';
import { DyslexiaScreenerListPageValidationSchema } from 'src/types/Validation/DyslexiaScreenerListPageValidationSchema';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import Modal from '../../../../components/Modal/Modal';
import Panel from '../../../../components/Panel/Panel';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import {
  clearDyslexiaScreenerListFiltersSnapshot,
  initialContactFormValues,
  selectDyslexiaScreenerContact,
  selectDyslexiaScreenerLeas,
  selectDyslexiaScreenerListFiltersSnapshot,
  selectDyslexiaScreenerListItems,
  selectDyslexiaScreenerListUpdateAllowed,
  selectDyslexiaScreenerLoadingContact,
  selectDyslexiaScreenerLoadingLeas,
  selectDyslexiaScreenerLoadingListItems,
  selectDyslexiaScreenerLoadingSchools,
  selectDyslexiaScreenerLoadingSchoolYears,
  selectDyslexiaScreenerSchools,
  selectDyslexiaScreenerSchoolYears,
  setDyslexiaScreenerListFiltersSnapshot,
  setDyslexiaScreenerPendingListRowContext,
} from '../../../../redux/slices/dyslexiaScreenerSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  createDyslexiaScreenerContact,
  fetchDyslexiaScreenerContact,
  fetchDyslexiaScreenerLeas,
  fetchDyslexiaScreenerListItems,
  fetchDyslexiaScreenerSchoolsByLeaId,
  fetchDyslexiaScreenerSchoolYears,
  updateDyslexiaScreenerContact,
} from '../../../../redux/thunks/dyslexiaScreenerThunks';
import { ButtonSize } from '../../../../types/ButtonSize';
import { DialogType } from '../../../../types/DialogTypes';
import { DyslexiaScreenerContactModel } from '../../../../types/DyslexiaScreenerContactModel';
import { PanelType } from '../../../../types/PanelType';
import { isPersistedLeaDyslexiaContactInformationComplete } from '../../../../utilities/dyslexiaScreenerUtilities';
import {
  isDistrictAdmin,
  isStateAdmin,
} from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './DyslexiaScreenerList.css';
import {
  dyslexiaScreenerListNotifications,
  getDyslexiaScreenerListCancelButtonTooltip,
  getDyslexiaScreenerListDisabledTableReason,
  getDyslexiaScreenerListDisabledTableTooltip,
  getDyslexiaScreenerListSaveButtonTooltip,
} from './dyslexiaScreenerListMessages';

const DyslexiaScreenerList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { schoolYear: urlSchoolYear } = useParams<{ schoolYear: string }>();
  const contact = useAppSelector(selectDyslexiaScreenerContact);
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const isLoadingContactList = useAppSelector(
    selectDyslexiaScreenerLoadingContact
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [resetFormFunction, setResetFormFunction] = useState<
    (() => void) | null
  >(null);
  const listItemsIsLoading = useAppSelector(
    selectDyslexiaScreenerLoadingListItems
  );
  const listItems: DyslexiaScreenerListDisplayRowModel[] = useAppSelector(
    selectDyslexiaScreenerListItems
  );
  const dyslexiaScreenerListUpdateAllowed = useAppSelector(
    selectDyslexiaScreenerListUpdateAllowed
  );
  const canEditContactForm =
    (isDistrictAdmin() || isStateAdmin()) && dyslexiaScreenerListUpdateAllowed;
  /** Contact fields editable for district/state admins only; filters/table use disableTableEdits. */
  const contactFormFieldsDisabled = !canEditContactForm || isSaving;
  const schoolYearOptions = useAppSelector(selectDyslexiaScreenerSchoolYears);
  const isLoadingSchoolYears = useAppSelector(
    selectDyslexiaScreenerLoadingSchoolYears
  );
  const leaOptions = useAppSelector(selectDyslexiaScreenerLeas);
  const isLoadingLeas = useAppSelector(selectDyslexiaScreenerLoadingLeas);
  const schoolOptions = useAppSelector(selectDyslexiaScreenerSchools);
  const isLoadingSchools = useAppSelector(selectDyslexiaScreenerLoadingSchools);
  const listFiltersSnapshot = useAppSelector(
    selectDyslexiaScreenerListFiltersSnapshot
  );
  const [selectedSchoolYearOption, setSelectedSchoolYearOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedLeaOption, setSelectedLeaOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedSchoolOption, setSelectedSchoolOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedScreenerTypeOption, setSelectedScreenerTypeOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedStatusOption, setSelectedStatusOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const screenerTypeOptions = [
    {
      value: DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_1,
      text: DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_1,
    },
    {
      value: DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_KG,
      text: DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_KG,
    },
    {
      value: DYSLEXIA_SCREENER_LIST_ROW_TYPE_TRAINING,
      text: DYSLEXIA_SCREENER_LIST_ROW_TYPE_TRAINING,
    },
  ];

  const statusFilterOptions = [
    {
      value: DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE,
      text: DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE,
    },
    {
      value: DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED,
      text: DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED,
    },
  ];

  const filteredListItems = listItems.filter((row) => {
    const leaIdentifier = (row.leaIdentifier || '').trim();
    const schoolIdentifier = (row.schoolIdentifier || '').trim();

    const matchesLea =
      !selectedLeaOption || selectedLeaOption.value === leaIdentifier;
    const matchesSchool =
      !selectedSchoolOption || selectedSchoolOption.value === schoolIdentifier;
    const matchesScreenerType =
      !selectedScreenerTypeOption ||
      row.type === selectedScreenerTypeOption.value;
    const matchesStatus =
      !selectedStatusOption || row.status === selectedStatusOption.value;

    return matchesLea && matchesSchool && matchesScreenerType && matchesStatus;
  });

  useEffect(() => {
    dispatch(fetchDyslexiaScreenerContact({ showLoading: true }));
    dispatch(fetchDyslexiaScreenerSchoolYears());
    dispatch(fetchDyslexiaScreenerLeas());
  }, [dispatch]);

  useEffect(() => {
    if (schoolYearOptions.length > 0) {
      if (!urlSchoolYear) {
        const mostRecentYear = schoolYearOptions[0].value;
        navigate(appRoutePaths.DyslexiaScreenerList(mostRecentYear), {
          replace: true,
        });
        return;
      }

      const urlSchoolYearOption = schoolYearOptions.find(
        (option) => option.value === urlSchoolYear
      );
      if (urlSchoolYearOption) {
        const selectedOption = {
          value: urlSchoolYearOption.value,
          label: urlSchoolYearOption.text,
        };
        setSelectedSchoolYearOption(selectedOption);
        dispatch(fetchDyslexiaScreenerListItems(selectedOption.value));
      } else {
        const mostRecentYear = schoolYearOptions[0].value;
        navigate(appRoutePaths.DyslexiaScreenerList(mostRecentYear), {
          replace: true,
        });
      }
    }
  }, [schoolYearOptions, urlSchoolYear, navigate, dispatch]);

  useEffect(() => {
    if (!listFiltersSnapshot) {
      return;
    }

    const finishRestore = (): void => {
      dispatch(clearDyslexiaScreenerListFiltersSnapshot());
    };

    if (listFiltersSnapshot.type) {
      const typeOption = screenerTypeOptions.find(
        (option) => option.value === listFiltersSnapshot.type
      );
      if (typeOption) {
        setSelectedScreenerTypeOption({
          value: typeOption.value,
          label: typeOption.text,
        });
      }
    } else {
      setSelectedScreenerTypeOption(null);
    }

    if (listFiltersSnapshot.status) {
      const statusOption = statusFilterOptions.find(
        (option) => option.value === listFiltersSnapshot.status
      );
      if (statusOption) {
        setSelectedStatusOption({
          value: statusOption.value,
          label: statusOption.text,
        });
      }
    } else {
      setSelectedStatusOption(null);
    }

    if (!listFiltersSnapshot.lea?.trim()) {
      setSelectedLeaOption(null);
      setSelectedSchoolOption(null);
      finishRestore();
      return;
    }

    if (leaOptions.length === 0) {
      return;
    }

    const leaMatch = leaOptions.find(
      (option) => option.value === listFiltersSnapshot.lea.trim()
    );
    if (!leaMatch) {
      setSelectedLeaOption(null);
      setSelectedSchoolOption(null);
      finishRestore();
      return;
    }

    if (selectedLeaOption?.value !== leaMatch.value) {
      setSelectedLeaOption({ value: leaMatch.value, label: leaMatch.text });
      dispatch(fetchDyslexiaScreenerSchoolsByLeaId(leaMatch.value));
      return;
    }

    if (!listFiltersSnapshot.school?.trim()) {
      setSelectedSchoolOption(null);
      finishRestore();
      return;
    }

    if (schoolOptions.length === 0) {
      return;
    }

    const schoolMatch = schoolOptions.find(
      (option) => option.value === listFiltersSnapshot.school.trim()
    );
    if (!schoolMatch) {
      setSelectedSchoolOption(null);
      finishRestore();
      return;
    }

    if (selectedSchoolOption?.value !== schoolMatch.value) {
      setSelectedSchoolOption({
        value: schoolMatch.value,
        label: schoolMatch.text,
      });
    }
    finishRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listFiltersSnapshot,
    leaOptions,
    schoolOptions,
    selectedLeaOption?.value,
    selectedSchoolOption?.value,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !isLoadingContactList &&
      !listItemsIsLoading &&
      !isLoadingSchoolYears &&
      !isLoadingLeas &&
      isInitialLoad
    ) {
      setIsInitialLoad(false);
    }
  }, [
    isLoadingContactList,
    listItemsIsLoading,
    isLoadingSchoolYears,
    isLoadingLeas,
    isInitialLoad,
  ]);

  const persistedLeaContactComplete =
    isPersistedLeaDyslexiaContactInformationComplete(contact);
  const tableDisabledReason = getDyslexiaScreenerListDisabledTableReason(
    isSaving,
    isFormDirty,
    persistedLeaContactComplete
  );
  const disableTableEdits = tableDisabledReason !== null;

  const getInitialValues = (): DyslexiaScreenerContactModel => {
    if (contact) {
      return {
        id: contact.id || '',
        leaIdentifier:
          contact.leaIdentifier || selectedDistrict.districtId || '',
        firstName: contact.firstName || '',
        lastOrSurname: contact.lastOrSurname || '',
        email: contact.email || '',
        telephone: contact.telephone || '',
      };
    }
    return {
      ...initialContactFormValues,
      leaIdentifier: selectedDistrict.districtId || '',
    };
  };

  const handleSubmit = async (
    values: DyslexiaScreenerContactModel,
    resetForm: () => void
  ): Promise<void> => {
    setIsSaving(true);
    try {
      const contactData: DyslexiaScreenerContactModel = {
        id: values?.id || '',
        leaIdentifier:
          values?.leaIdentifier || selectedDistrict.districtId || '',
        firstName: values?.firstName || '',
        lastOrSurname: values?.lastOrSurname || '',
        email: values?.email || '',
        telephone: values?.telephone || '',
      };

      if (contactData.id && contactData.id.trim() !== '') {
        await dispatch(updateDyslexiaScreenerContact(contactData)).unwrap();
        await dispatch(
          fetchDyslexiaScreenerContact({ showLoading: false })
        ).unwrap();
        dispatch(
          createNotification({
            severity: 'success',
            children: dyslexiaScreenerListNotifications.contactEdited,
          })
        );
      } else {
        await dispatch(createDyslexiaScreenerContact(contactData)).unwrap();
        await dispatch(
          fetchDyslexiaScreenerContact({ showLoading: false })
        ).unwrap();
        dispatch(
          createNotification({
            severity: 'success',
            children: dyslexiaScreenerListNotifications.contactSaved,
          })
        );
      }

      setIsFormDirty(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save contact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchoolYear = async (option: any): Promise<void> => {
    if (!option) {
      return;
    }
    setSelectedSchoolYearOption(option);
    dispatch(fetchDyslexiaScreenerListItems(option.value));
    navigate(appRoutePaths.DyslexiaScreenerList(option.value));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfLea = async (option: any): Promise<void> => {
    setSelectedLeaOption(option || null);
    setSelectedSchoolOption(null);
    if (option?.value) {
      dispatch(fetchDyslexiaScreenerSchoolsByLeaId(option.value));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchool = async (option: any): Promise<void> => {
    setSelectedSchoolOption(option || null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfScreenerType = async (option: any): Promise<void> => {
    setSelectedScreenerTypeOption(option || null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfStatus = async (option: any): Promise<void> => {
    setSelectedStatusOption(option || null);
  };

  const handleCancelClick = (): void => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = (): void => {
    if (resetFormFunction) {
      resetFormFunction();
    }
    setShowCancelModal(false);
  };

  const handleCancelCancel = (): void => {
    setShowCancelModal(false);
  };

  const navigateToDyslexiaScreenerInput = (
    row: DyslexiaScreenerListDisplayRowModel
  ): void => {
    const schoolYear = selectedSchoolYearOption?.value || '';
    const recordId = (row.recordId ?? '').trim();
    dispatch(
      setDyslexiaScreenerPendingListRowContext({
        leaIdentifier: row.leaIdentifier,
        districtName: row.districtName,
        schoolIdentifier: row.schoolIdentifier,
        schoolName: row.schoolName,
        schoolYear: row.schoolYear,
        kgIEPStudentCount: row.kgIEPStudentCount,
        grade1IEPStudentCount: row.grade1IEPStudentCount,
        kg504StudentCount: row.kg504StudentCount,
        grade1504StudentCount: row.grade1504StudentCount,
        kgExists: row.kgExists,
        grade1Exists: row.grade1Exists,
      })
    );
    dispatch(
      setDyslexiaScreenerListFiltersSnapshot({
        lea: selectedLeaOption?.value || '',
        school: selectedSchoolOption?.value || '',
        type: selectedScreenerTypeOption?.value || '',
        status: selectedStatusOption?.value || '',
      })
    );
    if (recordId) {
      navigate(appRoutePaths.DyslexiaScreenerInput(recordId, schoolYear));
    } else {
      navigate(appRoutePaths.DyslexiaScreenerInput('new', schoolYear));
    }
  };

  return (
    <DistrictSchoolInfoPage
      pageClass="data-input--district-school-info--dyslexia-screener--list"
      isLoading={
        isInitialLoad ||
        isLoadingContactList ||
        listItemsIsLoading ||
        isLoadingSchoolYears ||
        isLoadingLeas
      }
      loadingDataId={'dyslexia-screener-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Dyslexia Screener</h4>
            <span style={{ color: 'red' }}>* Required Field</span>
          </div>
          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={DyslexiaScreenerListPageValidationSchema}
              //eslint-disable-next-line @typescript-eslint/no-empty-function
              onSubmit={() => { }}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
            >
              {({ dirty, isValid, resetForm, values }) => {
                if (dirty !== isFormDirty) {
                  setIsFormDirty(dirty);
                }

                if (resetForm !== resetFormFunction) {
                  setResetFormFunction(() => resetForm);
                }

                return (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }}
                  >
                    <Panel
                      panelType={PanelType.INFO}
                      heading="LEA Dyslexia Contact Information"
                    >
                      <div className="contacts-container">
                        <div>
                          <div className="field-row">
                            <FormTextbox
                              displayName="First Name"
                              field="firstName"
                              disabled={contactFormFieldsDisabled}
                              validationSchema={
                                DyslexiaScreenerListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Last Name"
                              field="lastOrSurname"
                              disabled={contactFormFieldsDisabled}
                              validationSchema={
                                DyslexiaScreenerListPageValidationSchema
                              }
                            />
                          </div>
                          <div className="field-row">
                            <FormTextbox
                              displayName="Email"
                              field="email"
                              disabled={contactFormFieldsDisabled}
                              validationSchema={
                                DyslexiaScreenerListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Telephone Number"
                              field="telephone"
                              disabled={contactFormFieldsDisabled}
                              validationSchema={
                                DyslexiaScreenerListPageValidationSchema
                              }
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <ActionButton
                            classes="button"
                            size={ButtonSize.SMALL}
                            onClick={handleCancelClick}
                            dataTestId="cancel-button"
                            tooltipText={getDyslexiaScreenerListCancelButtonTooltip(
                              dirty,
                              isValid
                            )}
                            disabled={contactFormFieldsDisabled || !dirty}
                          >
                            <span>Cancel</span>
                          </ActionButton>
                          <ActionButton
                            classes="button--secondary"
                            size={ButtonSize.SMALL}
                            buttonType="button"
                            dataTestId="submit-button"
                            tooltipText={getDyslexiaScreenerListSaveButtonTooltip(
                              dirty,
                              isValid
                            )}
                            disabled={
                              contactFormFieldsDisabled || !isValid || !dirty
                            }
                            loading={isSaving}
                            onClick={async (
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.preventDefault();
                              e.stopPropagation();
                              await handleSubmit(values, resetForm);
                              return false;
                            }}
                          >
                            <span>Save</span>
                          </ActionButton>
                        </div>
                      </div>
                    </Panel>
                  </Form>
                );
              }}
            </Formik>
          </div>
          <br />
          <div>
            <div className="filters-selection-container">
              <div className="filters-dropdown-group">
                <div className="dropdown-selection">
                  <StyledSingleDropdown
                    fieldName="schoolYear"
                    options={schoolYearOptions}
                    onChange={handleOnChangeOfSchoolYear}
                    value={selectedSchoolYearOption}
                    isSearchable={false}
                    isClearable={false}
                    labelText="School Year"
                    showLabel={true}
                    disabled={disableTableEdits}
                    tooltipText={
                      disableTableEdits
                        ? getDyslexiaScreenerListDisabledTableTooltip(
                          'schoolYear',
                          tableDisabledReason
                        )
                        : 'Select school year'
                    }
                  />
                </div>
                <div className="dropdown-selection">
                  <StyledSingleDropdown
                    fieldName="lea"
                    options={leaOptions}
                    onChange={handleOnChangeOfLea}
                    value={selectedLeaOption}
                    isSearchable={true}
                    isClearable={true}
                    labelText="LEA"
                    showLabel={true}
                    disabled={disableTableEdits}
                    tooltipText={
                      disableTableEdits
                        ? getDyslexiaScreenerListDisabledTableTooltip(
                          'filter',
                          tableDisabledReason
                        )
                        : 'Select LEA'
                    }
                    placeholder="Select..."
                  />
                </div>
                <div className="dropdown-selection">
                  <StyledSingleDropdown
                    fieldName="school"
                    options={schoolOptions}
                    onChange={handleOnChangeOfSchool}
                    value={selectedSchoolOption}
                    isSearchable={true}
                    isClearable={true}
                    labelText="School"
                    showLabel={true}
                    disabled={
                      disableTableEdits ||
                      !selectedLeaOption ||
                      isLoadingSchools
                    }
                    tooltipText={
                      disableTableEdits
                        ? getDyslexiaScreenerListDisabledTableTooltip(
                          'filter',
                          tableDisabledReason
                        )
                        : 'Select school'
                    }
                    placeholder="Select..."
                  />
                </div>
                <div className="dropdown-selection">
                  <StyledSingleDropdown
                    fieldName="screenerType"
                    options={screenerTypeOptions}
                    onChange={handleOnChangeOfScreenerType}
                    value={selectedScreenerTypeOption}
                    isSearchable={false}
                    isClearable={true}
                    labelText="Type"
                    showLabel={true}
                    disabled={disableTableEdits}
                    tooltipText={
                      disableTableEdits
                        ? getDyslexiaScreenerListDisabledTableTooltip(
                          'filter',
                          tableDisabledReason
                        )
                        : 'Select type'
                    }
                    placeholder="Select..."
                  />
                </div>
                <div className="dropdown-selection">
                  <StyledSingleDropdown
                    fieldName="status"
                    options={statusFilterOptions}
                    onChange={handleOnChangeOfStatus}
                    value={selectedStatusOption}
                    isSearchable={false}
                    isClearable={true}
                    labelText="Status"
                    showLabel={true}
                    disabled={disableTableEdits}
                    tooltipText={
                      disableTableEdits
                        ? getDyslexiaScreenerListDisabledTableTooltip(
                          'filter',
                          tableDisabledReason
                        )
                        : 'Select status'
                    }
                    placeholder="Select..."
                  />
                </div>
              </div>
            </div>
            <div className="table-container">
              <table cellSpacing={0} className="page-view-table">
                <thead>
                  <tr>
                    <th>LEA</th>
                    <th>School</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredListItems?.length > 0 ? (
                    filteredListItems.map((row) => (
                      <tr key={row.rowId} data-testid={row.rowId}>
                        <td>{row.districtName}</td>
                        <td>{row.schoolName}</td>
                        <td>{row.type}</td>
                        <td>{row.status}</td>
                        <td>
                          <div className="action--column">
                            <button
                              data-testid={`edit-button-${row.rowId}`}
                              aria-label="Edit"
                              onClick={() =>
                                navigateToDyslexiaScreenerInput(row)
                              }
                              data-tooltip-id={TooltipAnchor}
                              data-tooltip-content={
                                disableTableEdits
                                  ? getDyslexiaScreenerListDisabledTableTooltip(
                                    'edit',
                                    tableDisabledReason
                                  )
                                  : 'Edit/View Dyslexia Screener'
                              }
                              disabled={disableTableEdits}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: disableTableEdits
                                  ? 'not-allowed'
                                  : 'pointer',
                                opacity: disableTableEdits ? 0.5 : 1,
                              }}
                            >
                              <img src={'/images/icon-edit.svg'} alt="Edit" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="no-results-row">
                      <td colSpan={5}>No results match your search criteria</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showCancelModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showCancelModal}
          onClose={handleCancelCancel}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleCancelConfirm}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleCancelCancel}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </DistrictSchoolInfoPage>
  );
};

export default DyslexiaScreenerList;
